import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetchData } from 'services/api/useFetchData';
import { EmailMessage, EmailRecipient } from '../../views/email/EmailTypes';
import { useModal } from 'components/modals/ModalContext';
import '../../style/scss/timeline.scss';

interface EmailMessageViewerProps {
    email: EmailMessage;
    mode: 'modal' | 'inline';
    sentByName?: string | null;
    sentDate?: string;
    collapsed?: boolean;
}

const EmailMessageViewer: React.FC<EmailMessageViewerProps> = ({
    email,
    mode,
    sentByName,
    sentDate,
    collapsed = false,
}) => {
    const { t } = useTranslation();
    const { revealModal } = useModal();
    const shadowRootRef = useRef<HTMLDivElement>(null);
    const [htmlContent, setHtmlContent] = useState<string | null>(null);

    const recipientsByType = useMemo(() => {
        const groupedRecipients: Record<string, EmailRecipient[]> = {
            to: [],
            cc: [],
            bcc: [],
        };

        if (email.recipients) {
            email.recipients.forEach(email_recipient => {
                if (groupedRecipients[email_recipient.type]) {
                    groupedRecipients[email_recipient.type].push(email_recipient);
                }
            });
        }

        return groupedRecipients;
    }, [email.recipients]);

    const formatRecipients = (recipients: EmailRecipient[]) => {
        return recipients.map(email_recipient => email_recipient.email_address).join(', ');
    };

    const requestParams = useMemo(() => ({
        ...(email.layout_id != null && { layout_id: String(email.layout_id) }),
        subject: email.subject ?? '',
        body: email.body ?? '',
    }), [email.layout_id, email.subject, email.body]);

    const { response } = useFetchData({ apiUrl: 'generate-email-layout-html', params: requestParams });

    useEffect(() => {
        if (response?.html) {
            setHtmlContent(response.html);

            if (mode === 'modal') {
                revealModal()
            }
        }
    }, [response]);

    useEffect(() => {
        if (shadowRootRef.current && htmlContent) {
            if (shadowRootRef.current.shadowRoot) {
                shadowRootRef.current.shadowRoot.innerHTML = htmlContent;
            } else {
                const shadowRoot = shadowRootRef.current.attachShadow({ mode: 'open' });
                shadowRoot.innerHTML = htmlContent;
            }
        }
    }, [htmlContent]);

    return (
        <div className='email-viewer'>
            <div className="email-header">
                <p>{t('email.general.to_short_label')}: {formatRecipients(recipientsByType.to) || '-'}</p>

                {recipientsByType.cc.length > 0 && (
                    <p>{t('email.general.cc_short_label')}: {formatRecipients(recipientsByType.cc)}</p>
                )}

                {recipientsByType.bcc.length > 0 && (
                    <p>{t('email.general.bcc_short_label')}: {formatRecipients(recipientsByType.bcc)}</p>
                )}

                <p>{t('email.general.from_short_label')}: {email.from_name ? `${email.from_name} <${email.from_email}>` : email.from_email}</p>                

                {email.subject && (
                    <p>{t('email.general.subject_label')}: {email.subject}</p>
                )}
            </div>

            <div className={`email-html-preview ${collapsed ? 'collapsed' : ''}`}>
                <div ref={shadowRootRef} />
            </div>

            {mode == "modal" && (sentDate || sentByName) && (
                <div className="email-footer">
                    {sentDate && <span>{sentDate}</span>}
                    {sentDate && sentByName && <span style={{ margin: '0 5px' }}>•</span>}
                    {sentByName && <span>{sentByName}</span>}
                </div>
            )}
        </div>
    );
};

export default EmailMessageViewer;